import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
    ShieldCheck,
    ShieldAlert,
    KeyRound,
    Lock,
    Globe2,
    AlertTriangle,
    CheckCircle2,
} from "lucide-react";

/**
 * Sovereign Shield · Bridge HUD
 * --------------------------------
 * Compact security posture indicator pinned to the top of the Master Command
 * Bridge. Reports three signals at a glance:
 *
 *   1. MFA  — pulled from /auth/mfa/status (definitive) with a fallback to
 *             user.mfa_enabled from /auth/me. Surfaces the Director's
 *             enrollment state directly.
 *   2. SSO  — pulled from /auth/sso. Reports cookie_domain so the Director
 *             can confirm cross-subdomain session sharing is armed.
 *   3. CSP  — Compliance Shield is enforced server-side. We probe by reading
 *             headers off a HEAD ping so the HUD reflects live posture rather
 *             than a hard-coded boolean.
 *
 * The HUD is intentionally non-blocking: if any signal fails to load it
 * degrades to "unknown" rather than rendering an error.
 */
export default function SovereignShield() {
    const { user } = useAuth();
    const [mfa, setMfa] = useState(null);
    const [sso, setSso] = useState(null);
    const [shieldOn, setShieldOn] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            // MFA enrollment status (authoritative)
            try {
                const { data } = await api.get("/auth/mfa/status");
                if (active) setMfa(data);
            } catch {
                if (active) setMfa({ mfa_enabled: !!user?.mfa_enabled, required_for_role: user?.role === "director" });
            }
            // SSO cookie-domain probe
            try {
                const { data } = await api.get("/auth/sso");
                if (active) setSso(data);
            } catch {
                if (active) setSso({ authenticated: false });
            }
            // Compliance Shield · live header probe
            try {
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/`;
                const res = await fetch(url, { method: "GET", credentials: "include" });
                const xfo = res.headers.get("x-frame-options");
                const csp = res.headers.get("content-security-policy");
                if (active) setShieldOn(Boolean(xfo === "DENY" && csp));
            } catch {
                if (active) setShieldOn(null);
            }
        })();
        return () => { active = false; };
    }, [user]);

    const isDirector = user?.role === "director";
    // Director-persona display: "Don Kir" is the Director's voice across the
    // platform; render it in the HUD so the Director sees their own callsign.
    const operatorName = isDirector ? "Don Kir" : (user?.name || user?.email || "Operator");

    const mfaOn = mfa?.mfa_enabled === true;
    const mfaRequired = mfa?.required_for_role === true;
    const mfaPending = mfa?.mfa_pending === true;

    // Posture: green when MFA on (or not required) AND Compliance Shield on.
    // Amber when MFA required but disabled. Red when SSO probe failed.
    let posture = "amber";
    let postureLabel = "Posture · partial";
    if (sso?.authenticated && shieldOn && (mfaOn || !mfaRequired)) {
        posture = "green";
        postureLabel = "Posture · fortified";
    } else if (!sso?.authenticated) {
        posture = "red";
        postureLabel = "Posture · breach risk";
    }

    const postureStyle = {
        green: { bg: "hsl(153 50% 25% / 0.55)", border: "hsl(153 50% 50% / 0.55)", dot: "hsl(153 65% 55%)", text: "hsl(153 60% 80%)" },
        amber: { bg: "hsl(40 70% 22% / 0.55)", border: "hsl(45 85% 55% / 0.45)", dot: "hsl(45 85% 65%)", text: "hsl(45 85% 78%)" },
        red:   { bg: "hsl(350 60% 22% / 0.55)", border: "hsl(350 75% 60% / 0.55)", dot: "hsl(350 75% 70%)", text: "hsl(350 70% 80%)" },
    }[posture];

    return (
        <div
            data-testid="sovereign-shield-hud"
            className="glass rounded-2xl px-5 py-4 mb-6 flex flex-wrap items-center gap-x-6 gap-y-3 fade-up"
            style={{ borderColor: postureStyle.border, background: postureStyle.bg }}
        >
            {/* Posture badge */}
            <div className="flex items-center gap-2 shrink-0">
                <span
                    className="w-2.5 h-2.5 rounded-full animate-pulse"
                    style={{ background: postureStyle.dot, boxShadow: `0 0 12px ${postureStyle.dot}` }}
                />
                <ShieldCheck size={18} className="text-[hsl(45_85%_68%)]" />
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_70%)]">Sovereign Shield</div>
                    <div className="text-xs font-semibold" style={{ color: postureStyle.text }} data-testid="shield-posture">
                        {postureLabel}
                    </div>
                </div>
            </div>

            {/* Operator callsign */}
            <div className="flex items-center gap-2 shrink-0 pl-4 border-l border-[hsl(40_40%_30%/0.25)]">
                <KeyRound size={14} className="text-[hsl(45_85%_68%)]" />
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Operator</div>
                    <div className="text-xs font-semibold text-foreground" data-testid="shield-operator">
                        {operatorName}
                    </div>
                </div>
            </div>

            {/* MFA indicator */}
            <div className="flex items-center gap-2 shrink-0 pl-4 border-l border-[hsl(40_40%_30%/0.25)]" data-testid="shield-mfa">
                <Lock size={14} className={mfaOn ? "text-[hsl(153_60%_60%)]" : "text-[hsl(45_85%_68%)]"} />
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">MFA</div>
                    {mfa === null ? (
                        <div className="text-xs text-muted-foreground">checking…</div>
                    ) : mfaOn ? (
                        <div className="text-xs font-semibold text-[hsl(153_60%_70%)]" data-testid="shield-mfa-on">
                            Armed · TOTP
                        </div>
                    ) : mfaPending ? (
                        <div className="text-xs font-semibold text-[hsl(45_85%_68%)]">Pending verification</div>
                    ) : mfaRequired ? (
                        <Link
                            to={isDirector ? "/app/director" : "/app/dashboard"}
                            className="text-xs font-semibold text-[hsl(45_85%_68%)] hover:text-[hsl(45_95%_75%)] inline-flex items-center gap-1"
                            data-testid="shield-mfa-enroll-cta"
                        >
                            <AlertTriangle size={11} /> Enroll required
                        </Link>
                    ) : (
                        <div className="text-xs text-muted-foreground">Optional · disabled</div>
                    )}
                </div>
            </div>

            {/* SSO indicator */}
            <div className="flex items-center gap-2 shrink-0 pl-4 border-l border-[hsl(40_40%_30%/0.25)]" data-testid="shield-sso">
                <Globe2 size={14} className={sso?.cookie_domain && sso.cookie_domain !== "host-only" ? "text-[hsl(153_60%_60%)]" : "text-[hsl(45_85%_68%)]"} />
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">SSO Domain</div>
                    {sso === null ? (
                        <div className="text-xs text-muted-foreground">checking…</div>
                    ) : !sso.authenticated ? (
                        <div className="text-xs font-semibold text-[hsl(350_70%_75%)]">Session lost</div>
                    ) : sso.cookie_domain === "host-only" ? (
                        <div className="text-xs font-semibold text-[hsl(45_85%_68%)]" title="Set COOKIE_DOMAIN=.bizboogie.com in production env">
                            host-only · preview
                        </div>
                    ) : (
                        <div className="text-xs font-semibold text-[hsl(153_60%_70%)] font-mono" data-testid="shield-sso-domain">
                            {sso.cookie_domain}
                        </div>
                    )}
                </div>
            </div>

            {/* Compliance Shield indicator */}
            <div className="flex items-center gap-2 shrink-0 pl-4 border-l border-[hsl(40_40%_30%/0.25)]" data-testid="shield-csp">
                {shieldOn === true ? (
                    <CheckCircle2 size={14} className="text-[hsl(153_60%_60%)]" />
                ) : shieldOn === false ? (
                    <ShieldAlert size={14} className="text-[hsl(350_70%_70%)]" />
                ) : (
                    <ShieldCheck size={14} className="text-muted-foreground" />
                )}
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Compliance Shield</div>
                    {shieldOn === null ? (
                        <div className="text-xs text-muted-foreground">probing…</div>
                    ) : shieldOn ? (
                        <div className="text-xs font-semibold text-[hsl(153_60%_70%)]">CSP · X-Frame · HSTS</div>
                    ) : (
                        <div className="text-xs font-semibold text-[hsl(350_70%_75%)]">Headers absent</div>
                    )}
                </div>
            </div>
        </div>
    );
}
